// App.js
import React from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "styled-components";
import { Provider } from "react-redux";
import { store } from "./store";
import { AuthProvider } from "./context/AuthContext";
import { lightTheme } from "./styles/theme";
import { GlobalStyles } from "./styles/GlobalStyles";
import { useAuth } from "./hooks/useAuth";
import "./config/firebase";

import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";

import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/AdminDashboard";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import Payment from "./pages/Payment";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import { NavbarProvider } from "./context/NavbarContext";
import LoadingScreen from "./components/common/loading/LoadingScreen";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Gallery from "./pages/Gallery";
import GalleryTopics from "./components/gallery/GalleryTopics";
import PhotoGrid from "./components/gallery/PhotoGrid";

// Public Route Component (redirects if already logged in)
const PublicRoute = ({ children }) => {
	const { user, loading } = useAuth();

	if (loading) {
		return <LoadingScreen loading={loading} />;
	}

	if (user) {
		return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} />;
	}

	return children;
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
	const { user, loading } = useAuth();

	if (loading) {
		return <LoadingScreen loading={loading} />;
	}

	if (!user) {
		return <Navigate to="/login" />;
	}

	if (requireAdmin && user.role !== "admin") {
		return <Navigate to="/dashboard" />;
	}

	return children;
};

const App = () => {
	return (
		<Provider store={store}>
			<ThemeProvider theme={lightTheme}>
				<GlobalStyles />
				<AuthProvider>
					<NavbarProvider>
						<Router>
							<div className="app-container">
								<Navbar />

								<main>
									<Routes>
										{/* Public Routes */}
										<Route path="/" element={<Home />} />
										<Route
											path="/login"
											element={
												<PublicRoute>
													<Login />
												</PublicRoute>
											}
										/>
										<Route
											path="/register"
											element={
												<PublicRoute>
													<Register />
												</PublicRoute>
											}
										/>

										<Route
											path="/forgot-password"
											element={
												<PublicRoute>
													<ForgotPassword />
												</PublicRoute>
											}
										/>

										{/* Protected Routes */}
										<Route
											path="/dashboard"
											element={
												<ProtectedRoute>
													<Dashboard />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin"
											element={
												<ProtectedRoute requireAdmin>
													<AdminDashboard />
												</ProtectedRoute>
											}
										/>

										{/* Payment Route */}
										<Route
											path="/payment"
											element={
												<ProtectedRoute>
													<Payment />
												</ProtectedRoute>
											}
										/>

										{/* Other routes */}
										<Route path="/about" element={<About />} />
										<Route path="/pricing" element={<Pricing />} />
										<Route path="/gallery" element={<Gallery />}>
											<Route index element={<GalleryTopics />} />
											<Route path=":topicId" element={<PhotoGrid />} />
										</Route>
										<Route path="/faq" element={<FAQ />} />

										<Route path="/contact" element={<Contact />} />

										<Route path="/privacy" element={<PrivacyPolicy />} />
										<Route path="/terms" element={<TermsOfService />} />
										<Route path="/cookies" element={<CookiePolicy />} />
									</Routes>
								</main>
								<Footer />
							</div>
						</Router>
						<Toaster position="top-right" />
					</NavbarProvider>
				</AuthProvider>
			</ThemeProvider>
		</Provider>
	);
};

export default App;
